import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Wallet,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Receipt,
  Upload,
  Phone,
  Fingerprint,
  ArrowRight,
  Sparkles,
  User,
  Mail,
  AtSign,
  CalendarDays,
} from "lucide-react";
import API_BASE_URL from "../config/Config.js";
import kaspiLogo from "../assets/kaspi_logo.png";
import { formatShortDate, formatCurrency } from "../utils/formatters";
import { PageLoader } from "./ui/Loader";
import GlassCard from "./ui/GlassCard";
import Button from "./ui/Button";

const STATUS_STYLES = {
  GREEN: {
    icon: CheckCircle2,
    ring: "ring-1 ring-primary-200",
    iconWrap: "bg-primary-100 text-primary-600",
    badge: "text-primary-700 bg-primary-100",
    glow: "shadow-glow",
    label: "Взносы оплачены",
  },
  YELLOW: {
    icon: AlertTriangle,
    ring: "ring-1 ring-amber-200",
    iconWrap: "bg-amber-100 text-amber-600",
    badge: "text-amber-700 bg-amber-100",
    glow: "shadow-[0_0_40px_rgba(245,158,11,0.25)]",
    label: "Есть задолженность",
  },
  RED: {
    icon: XCircle,
    ring: "ring-1 ring-red-200",
    iconWrap: "bg-red-100 text-red-600",
    badge: "text-red-700 bg-red-100",
    glow: "shadow-[0_0_40px_rgba(239,68,68,0.25)]",
    label: "Просрочка более 6 месяцев",
  },
  NONE: {
    icon: Wallet,
    ring: "ring-1 ring-dark-200",
    iconWrap: "bg-dark-100 text-dark-500",
    badge: "text-dark-600 bg-dark-100",
    glow: "",
    label: "",
  },
};

const PAYMENT_STATUS_LABEL = {
  PENDING: { label: "На проверке", className: "text-amber-700 bg-amber-100" },
  CONFIRMED: { label: "Подтверждён", className: "text-primary-700 bg-primary-100" },
  REJECTED: { label: "Отклонён", className: "text-red-700 bg-red-100" },
};

const FEE_LABEL = "2000 ₸ / месяц";

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

export default function Cabinet() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPayFlow, setShowPayFlow] = useState(false);
  const [phone, setPhone] = useState("");
  const [iin, setIin] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [iinInput, setIinInput] = useState("");
  const [savingIin, setSavingIin] = useState(false);
  const [iinError, setIinError] = useState("");

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/users/me/profile`, {
        headers: authHeaders(),
      });
      setProfile(res.data.data);
    } catch (err) {
      console.error("Ошибка загрузки данных аккаунта:", err);
    }
  }, []);

  const fetchMembership = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/users/me/membership`, {
        headers: authHeaders(),
      });
      setMembership(res.data.data);
    } catch (err) {
      console.error("Ошибка загрузки данных о взносах:", err);
      setError("Не удалось загрузить данные. Попробуйте обновить страницу.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }
    fetchProfile();
    fetchMembership();
  }, [navigate, fetchProfile, fetchMembership]);

  const handlePay = async (e) => {
    e.preventDefault();

    if (!receipt) {
      setError("Прикрепите чек об оплате — без него мы не сможем быстро подтвердить взнос.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("phone", phone);
      formData.append("iin", iin);
      formData.append("receipt", receipt);

      await axios.post(`${API_BASE_URL}/users/me/membership/pay`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setPaySuccess(true);
      setShowPayFlow(false);
      setReceipt(null);
      await Promise.all([fetchMembership(), fetchProfile()]);
    } catch (err) {
      console.error("Ошибка при отправке платежа:", err);
      setError(
        err.response?.data?.error || "Не удалось отправить запрос на оплату."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveIin = async (e) => {
    e.preventDefault();
    if (!/^\d{12}$/.test(iinInput)) {
      setIinError("ИИН должен содержать ровно 12 цифр");
      return;
    }
    setSavingIin(true);
    setIinError("");
    try {
      await axios.post(
        `${API_BASE_URL}/users/me/profile`,
        { iin: iinInput },
        { headers: authHeaders() }
      );
      await Promise.all([fetchMembership(), fetchProfile()]);
    } catch (err) {
      console.error("Ошибка при сохранении ИИН:", err);
      setIinError(err.response?.data?.error || "Не удалось сохранить ИИН.");
    } finally {
      setSavingIin(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  const status = membership?.status || "NONE";
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.NONE;
  const StatusIcon = statusStyle.icon;
  const needsPhoneIin = !membership?.phone || !membership?.iin;
  const payments = [...(membership?.payments || [])].reverse();

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header band */}
      <div className="relative overflow-hidden bg-gradient-hero pt-32 pb-20 px-6">
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 bg-primary-400/30 rounded-full blur-3xl animate-float-slow" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl animate-float" />

        <div className="relative max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-semibold tracking-wide uppercase rounded-full bg-white/15 text-white backdrop-blur-sm"
          >
            <Wallet className="w-4 h-4" />
            Личный кабинет
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-white"
          >
            Членские взносы
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-white/80 max-w-xl mx-auto"
          >
            Отслеживайте статус взносов, оплачивайте онлайн и следите за историей платежей.
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 -mt-10 pb-20 relative z-10">
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {!membership?.is_member ? (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-6 rounded-2xl p-4 md:p-5 flex items-start sm:items-center gap-3 border bg-amber-50 border-amber-200">
              <AlertTriangle className="w-6 h-6 shrink-0 text-amber-600" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-amber-800">Вы ещё не вступили в организацию</p>
                <p className="text-sm mt-0.5 text-amber-700">
                  Взнос не оплачен — оплатите {FEE_LABEL}, чтобы стать участником.
                </p>
              </div>
              <Button
                size="sm"
                variant="primary"
                onClick={() => setShowPayFlow(true)}
                className="shrink-0"
              >
                Стать участником
              </Button>
            </div>
          </motion.div>
        ) : (
          membership.debt > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div
                className={`mb-6 rounded-2xl p-4 md:p-5 flex items-start sm:items-center gap-3 border ${
                  status === "RED"
                    ? "bg-red-50 border-red-200"
                    : "bg-amber-50 border-amber-200"
                }`}
              >
                <StatusIcon
                  className={`w-6 h-6 shrink-0 ${
                    status === "RED" ? "text-red-600" : "text-amber-600"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-semibold ${
                      status === "RED" ? "text-red-800" : "text-amber-800"
                    }`}
                  >
                    {status === "RED"
                      ? "Серьёзная просрочка по взносам"
                      : "Есть задолженность по взносам"}
                  </p>
                  <p className={`text-sm mt-0.5 ${status === "RED" ? "text-red-700" : "text-amber-700"}`}>
                    Долг: {formatCurrency(membership.debt)}.{" "}
                    {status === "RED"
                      ? "Просрочка более 6 месяцев — пожалуйста, погасите задолженность как можно скорее."
                      : "В этом месяце взнос ещё не оплачен."}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={status === "RED" ? "primary" : "outline"}
                  onClick={() => setShowPayFlow(true)}
                  className="shrink-0"
                >
                  Оплатить
                </Button>
              </div>
            </motion.div>
          )
        )}

        {!membership?.iin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <GlassCard hover={false} className="mb-6 p-6 bg-white ring-1 ring-amber-200">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                  <Fingerprint className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-dark-900 font-semibold">Укажите ваш ИИН</p>
                  <p className="text-sm text-dark-500 mt-0.5">
                    Ваш аккаунт зарегистрирован без ИИН — он нужен, чтобы сопоставлять
                    ваши переводы взносов с выпиской банка. Укажите его один раз.
                  </p>
                </div>
              </div>
              <form onSubmit={handleSaveIin} className="flex flex-col sm:flex-row gap-3 sm:pl-[52px]">
                <input
                  type="text"
                  value={iinInput}
                  onChange={(e) => setIinInput(e.target.value)}
                  required
                  maxLength={12}
                  pattern="\d{12}"
                  placeholder="123456789012"
                  className="flex-1 rounded-lg border border-dark-200 px-4 py-2.5 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <Button type="submit" variant="primary" size="md" loading={savingIin}>
                  Сохранить
                </Button>
              </form>
              {iinError && (
                <p className="text-red-600 text-sm mt-2 sm:pl-[52px]">{iinError}</p>
              )}
            </GlassCard>
          </motion.div>
        )}

        {!membership?.is_member ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <GlassCard hover={false} className="p-8 md:p-10 bg-white text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center shadow-glow mb-5">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-dark-900 mb-2">
                Вы ещё не участник движения
              </h2>
              <p className="text-dark-500 max-w-md mx-auto mb-6">
                Станьте участником «Жасыл Желкен» и начните отслеживать свои членские
                взносы — <span className="font-semibold text-primary-700">{FEE_LABEL}</span>.
              </p>

              {!showPayFlow && !paySuccess && (
                <Button
                  variant="primary"
                  size="lg"
                  icon={<ArrowRight className="w-5 h-5" />}
                  onClick={() => setShowPayFlow(true)}
                >
                  Стать участником
                </Button>
              )}

              {paySuccess && <PaySuccessNote />}

              <AnimatePresence>
                {showPayFlow && (
                  <PayFlow
                    phone={phone}
                    setPhone={setPhone}
                    iin={iin}
                    setIin={setIin}
                    receipt={receipt}
                    setReceipt={setReceipt}
                    onSubmit={handlePay}
                    submitting={submitting}
                    needPhoneIin={needsPhoneIin}
                  />
                )}
              </AnimatePresence>
            </GlassCard>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <GlassCard
                hover={false}
                className={`mb-8 p-6 md:p-8 bg-white ${statusStyle.ring} ${statusStyle.glow}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${statusStyle.iconWrap}`}>
                      <StatusIcon className="w-6 h-6" />
                    </div>
                    <h2 className="text-lg font-bold text-dark-900">
                      Статус членских взносов
                    </h2>
                  </div>
                  <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${statusStyle.badge}`}>
                    {statusStyle.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatTile icon={Wallet} label="Начислено" value={formatCurrency(membership.accrued)} />
                  <StatTile icon={CheckCircle2} label="Оплачено" value={formatCurrency(membership.paid)} tone="primary" />
                  <StatTile
                    icon={AlertTriangle}
                    label="Задолженность"
                    value={formatCurrency(membership.debt)}
                    tone={membership.debt > 0 ? "danger" : "default"}
                  />
                  <StatTile
                    icon={Clock}
                    label="Период оплаты"
                    value={`по ${formatShortDate(membership.paid_until)}`}
                  />
                </div>

                <div className="mt-6 pt-6 border-t border-dark-100 flex justify-center">
                  {!showPayFlow && !paySuccess && (
                    <Button
                      variant="primary"
                      icon={<ArrowRight className="w-5 h-5" />}
                      onClick={() => setShowPayFlow(true)}
                    >
                      Оплатить взнос — {FEE_LABEL}
                    </Button>
                  )}
                  {paySuccess && <PaySuccessNote />}
                </div>

                <AnimatePresence>
                  {showPayFlow && (
                    <div className="max-w-md mx-auto mt-2">
                      <PayFlow
                        phone={phone}
                        setPhone={setPhone}
                        iin={iin}
                        setIin={setIin}
                        receipt={receipt}
                        setReceipt={setReceipt}
                        onSubmit={handlePay}
                        submitting={submitting}
                        needPhoneIin={needsPhoneIin}
                      />
                    </div>
                  )}
                </AnimatePresence>
              </GlassCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Receipt className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-bold text-dark-900">История платежей</h3>
              </div>

              <GlassCard hover={false} className="overflow-hidden bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-dark-50 text-dark-500">
                      <tr>
                        <th className="px-5 py-3 font-medium">Дата</th>
                        <th className="px-5 py-3 font-medium">Сумма</th>
                        <th className="px-5 py-3 font-medium">Статус</th>
                        <th className="px-5 py-3 font-medium">Чек</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => {
                        const st =
                          PAYMENT_STATUS_LABEL[p.status] || PAYMENT_STATUS_LABEL.PENDING;
                        return (
                          <tr key={p.id} className="border-t border-dark-100 hover:bg-dark-50/60 transition-colors">
                            <td className="px-5 py-3 text-dark-700">{formatShortDate(p.created_at)}</td>
                            <td className="px-5 py-3 font-medium text-dark-900">{formatCurrency(p.amount)}</td>
                            <td className="px-5 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.className}`}>
                                {st.label}
                              </span>
                            </td>
                            <td className="px-5 py-3">
                              {p.receipt_url ? (
                                <a
                                  href={p.receipt_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
                                >
                                  <Receipt className="w-4 h-4" />
                                  Открыть
                                </a>
                              ) : (
                                <span className="text-dark-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {payments.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-5 py-10 text-center text-dark-400">
                            Платежей пока нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </motion.div>
          </>
        )}

        {profile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8"
          >
            <GlassCard hover={false} className="p-6 md:p-8 bg-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center shadow-glow">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-dark-900">
                    {[profile.first_name, profile.last_name].filter(Boolean).join(" ") ||
                      profile.username}
                  </h2>
                  <p className="text-sm text-dark-400">Мои данные</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <ProfileField icon={AtSign} label="Логин" value={profile.username} />
                <ProfileField icon={Mail} label="Email" value={profile.email} />
                <ProfileField icon={Phone} label="Телефон" value={profile.phone || "—"} />
                <ProfileField icon={Fingerprint} label="ИИН" value={profile.iin || "—"} />
                <ProfileField
                  icon={CalendarDays}
                  label="Дата регистрации"
                  value={profile.created_at ? formatShortDate(profile.created_at) : "—"}
                />
              </div>
            </GlassCard>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function ProfileField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-dark-50 px-4 py-3">
      <div className="w-9 h-9 shrink-0 rounded-lg bg-white flex items-center justify-center text-primary-600">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-dark-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-dark-900 truncate">{value}</p>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone = "default" }) {
  const tones = {
    default: "bg-dark-50 text-dark-500",
    primary: "bg-primary-50 text-primary-600",
    danger: "bg-red-50 text-red-600",
  };
  const valueTones = {
    default: "text-dark-900",
    primary: "text-primary-700",
    danger: "text-red-600",
  };

  return (
    <div className={`rounded-xl p-4 ${tones[tone]}`}>
      <Icon className="w-4 h-4 mb-2 opacity-70" />
      <p className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${valueTones[tone]}`}>{value}</p>
    </div>
  );
}

function PaySuccessNote() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 text-amber-800 text-sm"
    >
      <Clock className="w-4 h-4 shrink-0" />
      Платёж отправлен на проверку администратору.
    </motion.div>
  );
}

function PayFlow({
  phone,
  setPhone,
  iin,
  setIin,
  receipt,
  setReceipt,
  onSubmit,
  submitting,
  needPhoneIin,
}) {
  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      onSubmit={onSubmit}
      className="mt-6 text-left overflow-hidden"
    >
      <div className="bg-dark-50 rounded-2xl p-5 space-y-5 border border-dark-100">
        {needPhoneIin && (
          <div className="space-y-4">
            <p className="text-sm text-dark-500">
              Укажите телефон и ИИН — это поможет администратору сопоставить
              ваш перевод с выпиской банка.
            </p>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-dark-700 mb-1">
                <Phone className="w-3.5 h-3.5" /> Телефон
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="+7 700 000 00 00"
                className="w-full rounded-lg border border-dark-200 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-dark-700 mb-1">
                <Fingerprint className="w-3.5 h-3.5" /> ИИН
              </label>
              <input
                type="text"
                value={iin}
                onChange={(e) => setIin(e.target.value)}
                required
                maxLength={12}
                placeholder="123456789012"
                className="w-full rounded-lg border border-dark-200 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 bg-white rounded-xl p-4 border border-dark-100">
          <img src={kaspiLogo} alt="Kaspi Bank" className="h-10 shrink-0" />
          <p className="text-sm text-dark-600">
            Переведите <strong className="text-dark-900">2000 ₸</strong> по{" "}
            <a
              href="https://kaspi.kz/pay/PalatyUniversal?6118=6&6119=23810&started_from=share"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 font-semibold underline underline-offset-2"
            >
              ссылке для взноса
            </a>{" "}
            или QR-коду на странице «Реквизиты».
          </p>
        </div>

        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-dark-700 mb-1">
            <Upload className="w-3.5 h-3.5" /> Чек об оплате (скриншот перевода)
          </label>
          <label className="flex items-center justify-center gap-2 w-full rounded-lg border-2 border-dashed border-primary-300 bg-primary-50/50 px-3 py-4 text-sm text-primary-700 font-medium cursor-pointer hover:bg-primary-50 transition-colors">
            <Upload className="w-4 h-4" />
            {receipt ? receipt.name : "Выбрать файл"}
            <input
              type="file"
              accept="image/*,application/pdf"
              required
              onChange={(e) => setReceipt(e.target.files?.[0] || null)}
              className="hidden"
            />
          </label>
          <p className="text-xs text-dark-400 mt-1.5">
            Приложите скриншот или фото чека — так администратор подтвердит взнос быстрее.
          </p>
        </div>

        <Button type="submit" variant="primary" className="w-full" loading={submitting}>
          Я оплатил
        </Button>
      </div>
    </motion.form>
  );
}
